import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import { QUIZ_LENGTH } from "../../index";

import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import StronglyDislikeIcon from "@mui/icons-material/MoodBad";
import DislikeIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import NeutralIcon from "@mui/icons-material/SentimentNeutral";
import LikeIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import StronglyLikeIcon from "@mui/icons-material/InsertEmoticon";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";

import { classes, BackgroundBox, QuizGrid, QuizRadioGroup } from "./style";
import { ProgressBar } from "../../util/progressBar";

import { questionsList, answersList } from "./content";

const PAGE_SIZE = 5;
const SHOW_QUESTION_PROGRESS = true;

export const getDisplayedQuestions = ({ questions, pageIndex, pageSize }) => {
  const firstIndex = pageIndex * pageSize;
  const lastIndex = firstIndex + pageSize;

  return questions.slice(firstIndex, lastIndex);
};

export const allPageQuestionsAnswered = (questions, answers) => {
  return questions.every((question) => answers[question.index - 1] > 0);
};

export const getProgressQuestions = ({ questions, userAnswers }) => {
  const completedAnswers = userAnswers.filter((answer) => Number(answer) !== 0);
  return Math.floor((completedAnswers.length / questions.length) * 100);
};

export const getProgressPages = ({ questions, pageIndex, pageSize }) =>
  Math.floor(((pageIndex + 1) / Math.ceil(questions.length / pageSize)) * 100);

export const isLastPage = ({ questions, pageIndex, pageSize }) =>
  pageIndex + 1 === Math.ceil(questions.length / pageSize);

export const getUserAnswerData = (userAnswers) =>
  Object.keys(userAnswers)
    .map((id) => userAnswers[id])
    .join("");

export const getLabel = (rating) => {
  switch (rating) {
    case 0:
      return "Strongly Dislike";
    case 1:
      return "Dislike";
    case 2:
      return "Neutral";
    case 3:
      return "Like";
    case 4:
      return "Strongly Like";
    default:
      return "";
  }
};

export const getIcon = (rating) => {
  switch (rating) {
    case 0:
      return <StronglyDislikeIcon color="text" />;
    case 1:
      return <DislikeIcon color="text" />;
    case 2:
      return <NeutralIcon color="text" />;
    case 3:
      return <LikeIcon color="text" />;
    case 4:
      return <StronglyLikeIcon color="text" />;
    default:
      return "";
  }
};

export const QuizScene = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMediumScreen = useMediaQuery(
    `(min-width:${localStorage.getItem("MD_SCREEN")})`
  );

  const questions = questionsList;
  const answerOptions = answersList;

  const [searchParams] = useSearchParams({});
  const [loaded, setLoaded] = useState(false);
  const [userAnswers, setUserAnswers] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [sessionStorageChanged, setSessionStorageChanged] = useState(false);

  const previousParams = useRef(sessionStorage.getItem("answers") || "");

  const displayedQuestions = getDisplayedQuestions({
    questions,
    pageIndex,
    pageSize: PAGE_SIZE,
  });

  const nextEnabled = allPageQuestionsAnswered(displayedQuestions, userAnswers);
  const showFinishButton = isLastPage({
    questions,
    pageIndex,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    if (questions.length > 0 && answerOptions.length > 0) {
      updatePageIndex({ params: sessionStorage.getItem("answers") });
      setLoaded(true);
    }
  }, [questions.length, answerOptions.length]);

  useEffect(() => {
    setSessionStorageChanged(false);
    const newAnswers =
      sessionStorage.getItem("answers") || "0".repeat(QUIZ_LENGTH);

    setUserAnswers(newAnswers.split(""));
  }, [searchParams, sessionStorageChanged]);

  useEffect(() => {
    if (sessionStorage.getItem("answers")) {
      previousParams.current = sessionStorage.getItem("answers");
    }
  }, [searchParams, sessionStorageChanged]);

  useEffect(() => {
    const params = sessionStorage.getItem("answers");
    if (params && params !== "random") {
      const index = params.indexOf("0");
      const nextQuestion = index >= 0 ? index + 1 : questions.length + 1;
      setTimeout(function () {
        setCurrentQuestion(nextQuestion);
      }, 50);
      if (nextQuestion / (pageIndex + 1) / PAGE_SIZE <= 1) {
        handleScroll(nextQuestion);
      } else if (index < 0) {
        handleScroll(questions.length);
      }
    }
  }, [pageIndex, searchParams, sessionStorageChanged, questions]);

  const progress = SHOW_QUESTION_PROGRESS
    ? getProgressQuestions({ questions, userAnswers })
    : getProgressPages({ questions, pageIndex, pageSize: PAGE_SIZE });

  const onChange = ({ target: { value } }) => {
    const { questionIndex, value: answerValue } = JSON.parse(value);
    const newAnswers = [...userAnswers];
    newAnswers[questionIndex - 1] = answerValue;

    sessionStorage.setItem("answers", newAnswers.join(""));

    setSessionStorageChanged(true);
    if (questionIndex === currentQuestion) {
      handleFocusChange(questionIndex + 1);
    }
  };

  const updatePageIndex = ({ params }) => {
    let pageIndex = Math.floor(params?.indexOf("0") / PAGE_SIZE);
    if (!pageIndex) pageIndex = 0;
    else if (params === "random" || pageIndex === -1) {
      pageIndex = QUIZ_LENGTH / PAGE_SIZE - 1;
    }
    setPageIndex(pageIndex);
  };

  const onNext = () => {
    let newPage = pageIndex + 1;
    setPageIndex(newPage);
  };

  const onPrev = () => {
    setPageIndex(pageIndex - 1);
  };

  const onRestart = async () => {
    navigate({
      pathname: "/quiz",
    });

    sessionStorage.setItem("answers", "0".repeat(QUIZ_LENGTH));
    setSessionStorageChanged(true);
    window.location.reload();
  };

  const handleFocusChange = (id) => {
    setTimeout(function () {
      setCurrentQuestion(id);
    }, 50);
    handleScroll(id);
  };

  const handleScroll = (id) => {
    const nextElement = document.getElementById(id);
    if (nextElement) {
      setTimeout(function () {
        nextElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  const handleSubmit = async () => {
    navigate({
      pathname: "/results",
    });
  };

  return (
    <>
      <BackgroundBox id="quiz">
        {loaded ? (
          <Container className={classes.container}>
            <QuizGrid
              container
              direction="column"
              alignItems="center"
              justifyContent="flex-start"
            >
              <Grid container className={classes.mainContainer}>
                <ProgressBar
                  progressText={`Question ${displayedQuestions[0].index}-${
                    displayedQuestions.slice(-1)[0].index
                  }/${questions.length}`}
                  progress={progress}
                  handleRestart={onRestart}
                />
                <Grid container className={classes.questionGrid}>
                  {displayedQuestions.map((question, q) => (
                    <Grid
                      id={question.index}
                      container
                      key={q}
                      className={classes.baseRow}
                      style={{
                        backgroundColor:
                          question.index === currentQuestion
                            ? theme.palette.background.default
                            : question.index > currentQuestion
                            ? theme.palette.background.level3
                            : theme.palette.primary.background,
                        opacity:
                          question.index > currentQuestion ? "50%" : "100%",
                      }}
                    >
                      <Grid
                        size={{ xs: 12 }}
                        sx={{ display: "flex" }}
                        className={classes.questionNumbers}
                      >
                        {question.index}.
                      </Grid>
                      <Grid
                        size={{ xs: 12 }}
                        className={`${classes.questionColumn} ${classes.questionText}`}
                      >
                        {question.text}
                      </Grid>
                      <Grid
                        size={{ xs: 12 }}
                        className={classes.answerContainer}
                      >
                        <QuizRadioGroup>
                          <RadioGroup
                            className={classes.answerGroup}
                            style={{
                              alignItems: isMediumScreen
                                ? "center"
                                : "baseline",
                            }}
                            onChange={onChange}
                          >
                            {answerOptions.map((answer, a) => (
                              <FormControlLabel
                                className={classes.labelCtrl}
                                key={q + a}
                                control={
                                  <Radio
                                    color="primary"
                                    key={`${q}${a}`}
                                    value={JSON.stringify({
                                      questionIndex: question.index,
                                      area: question.area,
                                      value: answer.value,
                                    })}
                                    checked={
                                      Number(
                                        userAnswers[question.index - 1]
                                      ) === Number(answer.value)
                                    }
                                    icon={getIcon(a)}
                                    name={`q${q}`}
                                  />
                                }
                                label={getLabel(a)}
                                labelPlacement="bottom"
                              />
                            ))}
                          </RadioGroup>
                        </QuizRadioGroup>
                      </Grid>
                      <Grid
                        size={{ xs: 12 }}
                        className={classes.questionDivider}
                      >
                        {(q + 1) % PAGE_SIZE !== 0 ? <Divider /> : null}
                      </Grid>
                    </Grid>
                  ))}
                  <Grid
                    container
                    justifyContent="space-evenly"
                    style={{ paddingTop: 13, width: "100%" }}
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      style={{ width: "150px" }}
                      disabled={pageIndex === 0 ? true : false}
                      onClick={onPrev}
                    >
                      Back
                    </Button>
                    <Button
                      disabled={!nextEnabled}
                      variant="contained"
                      color="primary"
                      style={{ width: "150px" }}
                      onClick={showFinishButton ? handleSubmit : onNext}
                    >
                      {showFinishButton ? "View Results" : "Next"}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </QuizGrid>
          </Container>
        ) : (
          <Container>
            <Stack spacing={1} sx={{ padding: "20px 0px" }}>
              <Skeleton
                variant="rounded"
                width="100%"
                height={60}
                style={{ marginBottom: "10px" }}
              />
              <Skeleton variant="rounded" width="100%" height={170} />
              <Skeleton variant="rounded" width="100%" height={170} />
              <Skeleton variant="rounded" width="100%" height={170} />
              <Skeleton variant="rounded" width="100%" height={170} />
              <Skeleton variant="rounded" width="100%" height={170} />
            </Stack>
          </Container>
        )}
      </BackgroundBox>
    </>
  );
};

export default QuizScene;
